import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
 Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { useCanonicalSpecialties, suggestSpecialtiesFn } from "@/hooks/useAllSpecialties";
import { cn } from "@/lib/utils";

type Suggestion = { slug: string; label: string; matchedTerm: string | null; count: number };

const DEBOUNCE_MS = 150;

// Server-backed suggestions understand synonyms the label list can't: "retina"
// -> Ophthalmologists, "braces" -> Orthodontists, "pediatra" -> Pediatricians,
// and typos via trigram ("opthamologist"). Local substring filtering stays as
// the instant first paint and as the offline fallback.
function useSuggestions(query: string, enabled: boolean) {
  const [remote, setRemote] = useState<Suggestion[]>([]);
  const seq = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length < 2) { setRemote([]); return; }
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      suggestSpecialtiesFn({ data: { q, limit: 8 } })
        // Ignore out-of-order responses so a slow early keystroke can't
        // overwrite results for what the user has since typed.
        .then((rows) => { if (mine === seq.current) setRemote(rows as Suggestion[]); })
        .catch(() => { if (mine === seq.current) setRemote([]); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, enabled]);

  return remote;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

export function SpecialtySelect({
  value,
  onChange,
  placeholder,
  allowCustom = true,
  icon: Icon,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const specialties = useCanonicalSpecialties();
  const remote = useSuggestions(query, open);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const local = (q ? specialties.filter((s) => s.label.toLowerCase().includes(q)) : specialties)
      .map((s) => ({ ...s, hint: null as string | null }));
    if (!q) return local.slice(0, 200);
    // Local substring hits first (they render instantly and are what the user
    // is most likely reaching for), then anything only the server matched —
    // the synonym and typo hits.
    const seen = new Set(local.map((s) => s.slug));
    const extra = remote
      .filter((r) => !seen.has(r.slug))
      .map((r) => ({ slug: r.slug, label: r.label, count: r.count, hint: r.matchedTerm }));
    return [...local, ...extra].slice(0, 200);
  }, [query, specialties, remote]);

  const showCustom =
    allowCustom &&
    query.trim().length > 0 &&
    !specialties.some((s) => s.label.toLowerCase() === query.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-slot="select-trigger"
          className={cn(
            "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap border-0 bg-transparent px-0 py-2 text-sm shadow-none outline-none focus:ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer",
            className
          )}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {Icon && <Icon className="h-4 w-4 text-primary shrink-0" />}
            <span className={cn("truncate text-left", !value && "text-muted-foreground")}>
              {value || placeholder || "Select specialty…"}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[320px] max-w-[400px] p-0 rounded-xl border border-border/80 shadow-xl overflow-hidden bg-popover text-popover-foreground"
        align="start"
        sideOffset={8}
      >
        <Command shouldFilter={false} className="w-full bg-transparent">
          <CommandInput
            placeholder="Search specialty…"
            value={query}
            onValueChange={setQuery}
            className="border-0"
          />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">No match found.</CommandEmpty>
            <CommandGroup className="p-1.5">
              {items.map((s) => (
                <CommandItem
                  key={s.slug}
                  value={s.slug}
                  onSelect={() => {
                    onChange(s.label);
                    setOpen(false);
                    setQuery("");
                  }}
                  className="cursor-pointer py-2.5 px-3 rounded-lg text-sm font-normal aria-selected:bg-primary/10 aria-selected:text-primary transition-colors flex items-center gap-2"
                >
                  <Check className={cn("h-4 w-4 text-primary shrink-0 transition-opacity", value === s.label ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{s.label}</span>
                  {s.hint && (
                    <span className="text-xs text-muted-foreground truncate">· {s.hint}</span>
                  )}
                  {s.count > 0 && (
                    <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                      {formatCount(s.count)}
                    </span>
                  )}
                </CommandItem>
              ))}
              {showCustom && (
                <CommandItem
                  value={`__custom_${query}`}
                  onSelect={() => {
                    onChange(query.trim());
                    setOpen(false);
                    setQuery("");
                  }}
                  className="cursor-pointer py-2.5 px-3 rounded-lg text-sm font-normal aria-selected:bg-primary/10 aria-selected:text-primary transition-colors flex items-center gap-2"
                >
                  <Check className="h-4 w-4 opacity-0 shrink-0" />
                  <span className="truncate">Use “{query.trim()}”</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
